import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Video } from 'lucide-react';
import { meetingApi } from '@/features/meetings/meeting.api';
import { useAuthContext } from '@/features/auth/AuthProvider';
import { extractError } from '@/lib/axios';
import { config } from '@/config/env';

export default function JoinPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, bootstrapping } = useAuthContext();

  const initialCode = params.get('code') ?? params.get('invite') ?? '';
  const [code, setCode] = useState(initialCode);
  const [password, setPassword] = useState('');
  const [guestName, setGuestName] = useState('');
  const [info, setInfo] = useState<{ hasPassword: boolean; title: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!initialCode) return;
    meetingApi
      .joinInfo(initialCode)
      .then((i) => setInfo({ hasPassword: i.hasPassword, title: i.title }))
      .catch(() => setInfo(null));
  }, [initialCode]);

  const onJoin = async () => {
    if (!code) return toast.error('Enter a meeting code');
    setLoading(true);
    try {
      const res = await meetingApi.join(code, {
        password: password || undefined,
        guestName: !isAuthenticated ? guestName || undefined : undefined,
      });
      if (res.waiting) {
        navigate(`/meeting/${res.meetingId}?waiting=1&participant=${res.participant.id}`);
      } else {
        navigate(`/meeting/${res.meetingId}?participant=${res.participant.id}`);
      }
    } catch (err) {
      toast.error(extractError(err).message);
    } finally {
      setLoading(false);
    }
  };

  if (bootstrapping) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-xl font-bold">
          <Video className="text-brand-600" /> {config.appName}
        </div>
        <div className="card space-y-4">
          <h1 className="text-xl font-bold">{info?.title ? `Join "${info.title}"` : 'Join a meeting'}</h1>
          <div>
            <label className="mb-1 block text-sm font-medium">Meeting code</label>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. abc123xyz" />
          </div>
          {!isAuthenticated && (
            <div>
              <label className="mb-1 block text-sm font-medium">Your name</label>
              <input className="input" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </div>
          )}
          {(info?.hasPassword ?? true) && (
            <div>
              <label className="mb-1 block text-sm font-medium">Password (if required)</label>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          <button className="btn-primary w-full" onClick={onJoin} disabled={loading}>
            {loading ? 'Joining…' : 'Join meeting'}
          </button>
        </div>
      </div>
    </div>
  );
}
